import { Button, Upload, UploadFile, UploadProps } from "antd";
import { UploadChangeParam } from "antd/es/upload";
import { ReactNode, useEffect, useState } from "react";
// import FileIcon from "../../assets/icons/FileIcon";
import ImageCloseIcon from "../../assets/icons/ImageCloseIcon";
import AXIOS from "../../config/api";
import { BASE_API } from "../../constants/apiUrls";
import { IMAGE_URL } from "../../constants/constants";
import { toast } from "../../lib/toast";
import { extension, stringToArray } from "../../utils/array";
import { sliceString } from "../../utils/string";
import { PDF } from "../../utils/utils";

type Props = {
  errorMessage?: string;
  form: any;
  name: any;
  disabled?: boolean;
  fileType?: boolean;
  isPreview?: boolean;
  isGrid?: boolean;
  accept?: string;
  rootClassName?: string;
  imgClassName?: string;
  fileExtra?: ReactNode;
  showTextCount?: number;
  onChange?: (url: string | null) => void;
  listFile?: boolean;
  removeFile?: string;
} & Omit<UploadProps, "name">;

export default function FileUpload({
  errorMessage = "File upload fail",
  form,
  name,
  disabled,
  fileType,
  isPreview,
  isGrid,
  fileExtra,
  rootClassName = "",
  imgClassName = "",
  onChange,
  accept = ".png,.jpeg,jpg,.svg",
  showTextCount = 12,
  listFile = false,
  removeFile,
  ...rest
}: Props) {
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("No file chosen");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [imgUrl, setImgUrl] = useState("");

  const handleUpload = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    setLoading(true);

    try {
      const response = await AXIOS.post(`${BASE_API.fileUpload}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const url = response?.data?.endpoint;

      if (response.status === 201) {
        setFileName(
          `${sliceString(url, showTextCount, "...")}.${extension(url)}`
        );

        onChange && onChange(url);
        setImgUrl(url);
        setLoading(false);
      }
    } catch (error) {
      toast("error", errorMessage);
      setLoading(false);
    }

    return false;
  };

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setFileList(info.fileList);

    if (info.file.status === "removed") {
      onChange && onChange(null);
      setImgUrl("");
      setFileName("");
    }
  };

  const handleRemovePreviewImage = () => {
    onChange && onChange(null);
    setImgUrl("");
    setFileName("");
  };

  useEffect(() => {
    if (fileType) {
      const timer = setTimeout(() => {
        const file = form.getFieldValue(
          listFile ? stringToArray(name, ".") : [name]
        );

        if (file) {
          onChange && onChange(file);
          setImgUrl(file);

          setFileName(
            `${sliceString(file, showTextCount, "...")}.${extension(file)}`
          );
        }
      }, 1000);

      const file = form.getFieldValue(
        listFile ? stringToArray(name, ".") : [name]
      );

      if (file) {
        onChange && onChange(file);
        setImgUrl(file);

        setFileName(
          `${sliceString(file, showTextCount, "...")}.${extension(file)}`
        );
      } else {
        onChange && onChange("");
        setImgUrl("");
        setFileName("");
      }

      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, name, fileType, listFile, showTextCount]);

  useEffect(() => {
    if (removeFile === imgUrl) {
      setImgUrl("");
      setFileName("");
    }
  }, [imgUrl, removeFile]);

  return (
    <div className={isGrid ? "grid grid-cols-2 gap-4" : rootClassName}>
      <Upload
        name="file"
        beforeUpload={handleUpload}
        maxCount={1}
        accept={accept}
        className="custom-upload"
        showUploadList={false}
        onChange={handleChange}
        fileList={fileList}
        disabled={disabled}
        style={{ width: "100%" }}
        {...rest}
      >
        <Button
          className="flex items-center !justify-start !p-0"
          size="large"
          disabled={disabled}
          style={{ width: "100%" }}
        >
          {/* <FileIcon /> */}
          <span className="!flex justify-center items-center px-2.5 bg-accent text-white h-full">
            Browse file
          </span>
          <div className="flex items-center pl-2 gap-2 text-sm font-medium leading-normal text-others-accent">
            {/* <span className="border-r border-solid border-l-0 border-y-0 border-others-border h-4" /> */}
            {loading ? "Uploading..." : fileName || "No file chosen"}
          </div>
        </Button>
        {fileExtra}
      </Upload>
      {isPreview ? (
        imgUrl ? (
          <div
            className={`w-[76px] h-[80px] rounded-sm relative ${
              extension(imgUrl) !== "pdf"
                ? "bg-status-waiting-bg h-[73px] "
                : ""
            } ${isGrid ? "-mt-4" : "mt-4"} ${imgClassName}`}
          >
            <img
              className={`w-full h-full rounded-sm ${
                extension(imgUrl) === "pdf" ? "object-contain" : "object-cover"
              }`}
              src={extension(imgUrl) === "pdf" ? PDF : IMAGE_URL + imgUrl}
              alt=""
            />
            <button
              className="p-0 m-0 border-transparent w-0 h-0 rounded-full absolute right-2 -top-3.5 bg-white cursor-pointer"
              type="button"
              onClick={handleRemovePreviewImage}
            >
              <ImageCloseIcon />
            </button>
          </div>
        ) : isGrid ? (
          <div />
        ) : null
      ) : null}
    </div>
  );
}
